import { useCallback, useMemo } from 'react';
import toLower from 'lodash/toLower';

import Rect from '@lyearn/core/components/Rect';
import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import SortAndFilter from '@/components/FilterManager/SortAndFilter';
import { FULL_SCREEN_MODAL_CLASSES } from '@/components/FullScreenModalStyles';
import Loader from '@/components/Loader';
import { ModalVariant } from '@/components/Modals/types';
import Table from '@/components/Table';
import { commonTableStyles } from '@/components/Table/styles';
import AdminEmptyState from '@/modules/corporate/modules/admin/components/EmptyState';
import { RelatedEntityTypeEnum } from '@/types/schema';

import useAddTasks from '../../hooks/useAddTasks';
import { getTableColumns } from '../../utils';
import { useAddTaskModalContext } from '../AddTaskModalProvider';
import TaskRow from '../TaskRow';
import { headerStyles, rowStyles } from './styles';

const ENTITY_TYPE_LABEL_MAP: Record<string, string> = {
  [RelatedEntityTypeEnum.KeyResult]: 'Task:KeyResult',
  [RelatedEntityTypeEnum.Okr]: 'Task:Objective',
  [RelatedEntityTypeEnum.MeetingInstance]: 'Task:OneOnOne',
  [RelatedEntityTypeEnum.AgendaItem]: 'Task:OneOnOne',
};
interface TaskListModalProps {
  onClose: () => void;
  entityId?: string;
  entityType?: RelatedEntityTypeEnum;
  onSubmit: (refetchData?: boolean) => void;
}

function TaskListModal(props: TaskListModalProps) {
  const { onClose, entityId, entityType, onSubmit } = props;
  const { t } = useTranslation();
  const { data, filterManager, sortAndFilterItems, onAdd, isAddingTasks, selectedRootTasksCount } =
    useAddTasks({
      entityId,
      entityType,
      onSubmit,
      onClose,
    });
  const { methods } = useAddTaskModalContext();
  const { isItemSelected, onChange } = methods;
  const { fetching, error, actionItems, fetchMore, refetch, isFetchingMore } = data;

  const tableColumns = useMemo(() => getTableColumns(t), [t]);

  const onTaskRowClick = useEvent((taskId: string) => {
    onChange(taskId, !isItemSelected(taskId));
  });

  const rowRenderer = useCallback(
    ({ cells, rowData }: any) => {
      return <TaskRow cells={cells} rowData={rowData} onClick={onTaskRowClick} />;
    },
    [onTaskRowClick],
  );

  const renderAdaptedContents = () => {
    if (actionItems?.length) {
      return (
        <Rect>
          {({ width, height }) => (
            <Table
              className={commonTableStyles}
              columns={tableColumns}
              data={actionItems}
              headerClassName={cn(headerStyles, 'sticky top-0')}
              headerHeight={46}
              height={height}
              ignoreFunctionInColumnCompare={false}
              // @ts-ignore
              rowClassName={({ rowData }) =>
                cn('border-none flex-shrink-0', rowStyles, {
                  selected: isItemSelected(rowData.id),
                })
              }
              rowHeight={46}
              rowKey="id"
              rowRenderer={rowRenderer}
              width={width}
              onEndReached={fetchMore}
              onEndReachedThreshold={1000}
            />
          )}
        </Rect>
      );
    }

    if (fetching) {
      return <Loader />;
    }

    return (
      <AdminEmptyState
        clearFilters={filterManager.methods.clearFilters}
        clearSearch={filterManager.methods.clearSearch}
        description={t('Task:NoTasksDescription')}
        filter={filterManager.state.filter}
        search={filterManager.state.search}
        showIllustrationIcon={false}
        title={t('Task:NoTasksAvailable')}
      />
    );
  };

  return (
    <ContentListModal.Main
      action={{
        discardText: t('Common:Actions.Cancel'),
        onSubmit: onAdd,
        text: t('Common:Actions.Add'),
        isSubmitting: isAddingTasks,
        disabled: isAddingTasks,
        TertiaryAction: (
          <Typography color="text-text-placeholder" variant="label-02">
            {t('Task:TaskSelected', { count: selectedRootTasksCount })}
          </Typography>
        ),
      }}
      classes={FULL_SCREEN_MODAL_CLASSES}
      fetchMore={fetchMore}
      header={{
        title: t('Task:AddTasks'),
        description: t('Task:AddTasksDescription', {
          entityName: toLower(t(ENTITY_TYPE_LABEL_MAP[entityType!])),
        }),
      }}
      items={actionItems}
      searchBar={{
        value: filterManager.state.search,
        onChange: filterManager.methods.setSearch,
        selectedCount: 0,
        placeholder: t('Common:Search'),
      }}
      sortComponent={
        <>
          {sortAndFilterItems ? (
            <SortAndFilter
              className="mt-20 mb-12 px-8"
              filter={filterManager.state.filter}
              setFilter={filterManager.methods.setFilter}
              setSort={filterManager.methods.setSort}
              sort={filterManager.state.sort}
              sortAndFilterItems={sortAndFilterItems}
            />
          ) : undefined}
        </>
      }
      variant={ModalVariant.FullScreen}
      onClose={onClose}>
      <Stack className="h-full w-full flex-1 ps-24 pe-32">{renderAdaptedContents()}</Stack>
    </ContentListModal.Main>
  );
}

export default TaskListModal;
